/**
 * Which table, if any, is open in the table editor.
 *
 * Shared at module scope because two things far apart ask for the editor: the
 * button in the table's inspector, and a double click on the table on the
 * page. Neither owns the other, and threading an event from a block on the
 * canvas up to the inspector beside it would run through half the builder.
 *
 * The inspector says it is there for a table while it is mounted, and a request
 * for a table no inspector is showing is refused. Otherwise a double click on
 * a preview somewhere else would leave a request waiting, and the dialog would
 * spring open the next time that table happened to be selected.
 */

import { readonly, ref } from 'vue'

const openId = ref<string | null>(null)
const present = new Map<string, number>()

export function useTableEditor() {
  /** Open the editor on a table, if its inspector is showing. Says whether it did. */
  function open(id: string): boolean {
    if (!present.has(id)) return false
    openId.value = id
    return true
  }

  function close(): void {
    openId.value = null
  }

  /**
   * An inspector showing this table, from now until the returned function is
   * called. Counted, so a second inspector for the same table going away does
   * not take the first one's claim with it.
   */
  function attend(id: string): () => void {
    present.set(id, (present.get(id) ?? 0) + 1)
    return () => {
      const left = (present.get(id) ?? 1) - 1
      if (left > 0) present.set(id, left)
      else present.delete(id)
      if (!present.has(id) && openId.value === id) openId.value = null
    }
  }

  return { openId: readonly(openId), open, close, attend }
}
